import * as React from "react";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { CustomerTaxResidencyModel } from "../../../../../../../Wrap/Models/Models/Customer/Regulatory/CustomerTaxResidencyModel";
import { MifirCountryIdentifier } from "../../../../../../../Wrap/Models/Domains/Regulations/MifirCountryIdentifier";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { MifirIdentifierType } from "../../../../../../../Wrap/Constants/Enums/Domain/MifirIdentifierType";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { NationalInsuranceNumber } from "../../../../../../../react-core/Html/Validation/RegExPatterns";

interface INationalIdentifierProps extends IProcessComponentProps {
    data: CustomerTaxResidencyModel;
    onNationalIdentifierChange: (identifierType: MifirIdentifierType, value: string) => void;
    disabled?: boolean;
    identifiers: MifirCountryIdentifier[];
    isEdit: boolean;
}

interface INationalIdentifierState {
    dontOwnThis: boolean[];
    renderThreshold: number;
}

export class NationalIdentifier extends ProcessComponent<INationalIdentifierProps, INationalIdentifierState> {
    constructor(props: INationalIdentifierProps) {
        super(props);
        this.state = { dontOwnThis: [], renderThreshold: 0 };
    }

    componentDidMount() {
        super.componentDidMount();
        const { identifiers, data, isEdit } = this.props;
        const _dontOwnThis = [];
        let _renderThreshold = 0;
        const clientOwnsTheFirstIdentifier =
            identifiers.length > 0 ? this.clientOwnThisIdentifier(data, identifiers[0]) : false;
        identifiers.forEach((config, i) => {
            const mandatory =
                i == identifiers.length - 1 && config?.Country != CountryCode.ESP && config?.Country != CountryCode.POL;
            if (
                (config.IdentifierPriority == 1 || !clientOwnsTheFirstIdentifier) &&
                config.IdentifierType !== MifirIdentifierType.Concat
            ) {
                const nationalityIdentifier = this.clientTaxIdentifier(data, config, mandatory);
                if (nationalityIdentifier == "") {
                    _dontOwnThis[i] = true;
                    _renderThreshold++;
                } else if (nationalityIdentifier == null && isEdit) {
                    _dontOwnThis[i] = true;
                    _renderThreshold++;
                } else {
                    _dontOwnThis[i] = false;
                }
            }
        });

        this.setState({ dontOwnThis: _dontOwnThis, renderThreshold: _renderThreshold });
    }

    UNSAFE_componentWillReceiveProps(nextProps: INationalIdentifierProps) {
        if (!this.state.dontOwnThis.length && nextProps.identifiers.length) {
            const { identifiers, data, isEdit } = nextProps;
            const _dontOwnThis = [];
            const clientOwnsTheFirstIdentifier =
                identifiers.length > 0 ? this.clientOwnThisIdentifier(data, identifiers[0]) : false;
            identifiers.forEach((config, i) => {
                const mandatory =
                    i == identifiers.length - 1 &&
                    config?.Country != CountryCode.ESP &&
                    config?.Country != CountryCode.POL;
                if (
                    (config.IdentifierPriority == 1 || !clientOwnsTheFirstIdentifier) &&
                    config.IdentifierType !== MifirIdentifierType.Concat
                ) {
                    const nationalityIdentifier = this.clientTaxIdentifier(data, config, mandatory);
                    if (nationalityIdentifier == "") {
                        _dontOwnThis[i] = true;
                    } else if (nationalityIdentifier == null && isEdit) {
                        _dontOwnThis[i] = true;
                    } else {
                        _dontOwnThis[i] = false;
                    }
                }
            });

            this.setState({ dontOwnThis: _dontOwnThis });
        }
    }

    clientOwnThisIdentifier(data: CustomerTaxResidencyModel, mifirCountryIdentifier: MifirCountryIdentifier): boolean {
        switch (mifirCountryIdentifier.IdentifierType) {
            case MifirIdentifierType.PassportNumber:
                return !CommonHelper.isNullOrEmpty(data.PassportNumber);
            case MifirIdentifierType.NationalIdentifier:
                return !CommonHelper.isNullOrEmpty(data.NationalIdentifier);
            case MifirIdentifierType.TaxNumber:
                return !CommonHelper.isNullOrEmpty(data.TaxReference);
            default:
                return false;
        }
    }

    clearData(currentIndex: number) {
        const { identifiers, data } = this.props;
        const clientOwnsTheFirstIdentifier =
            identifiers.length > 0 ? this.clientOwnThisIdentifier(data, identifiers[0]) : false;
        for (let i = currentIndex; i < identifiers.length; i++) {
            const config = identifiers[i];
            if (
                (config.IdentifierPriority == 1 || !clientOwnsTheFirstIdentifier) &&
                config.IdentifierType !== MifirIdentifierType.Concat
            ) {
                this.props.onNationalIdentifierChange(config.IdentifierType, null);
            }
        }
    }

    render() {
        const { identifiers, data, disabled } = this.props;
        const { dontOwnThis, renderThreshold } = this.state;
        const result = [];
        const clientOwnsTheFirstIdentifier =
            identifiers.length > 0 ? this.clientOwnThisIdentifier(data, identifiers[0]) : false;

        identifiers.forEach((config, i) => {
            if (
                (config.IdentifierPriority == 1 || !clientOwnsTheFirstIdentifier) &&
                config.IdentifierType !== MifirIdentifierType.Concat
            ) {
                const mandatory = i == identifiers.length - 1 && config?.Country != CountryCode.ESP;
                if (i <= renderThreshold) {
                    result.push(
                        <>
                            <TextBoxEditor
                                label={config.IdentifierTypeWording}
                                cssWrapper="control-size-430"
                                alwaysValidateOnUpdate={true}
                                value={this.clientTaxIdentifier(data, config, mandatory)}
                                placeholder={
                                    config.IdentifierType == MifirIdentifierType.PassportNumber
                                        ? ""
                                        : config.IdentifierType == MifirIdentifierType.NationalIdentifier
                                        ? "Please enter national identifier..."
                                        : "Please enter tax identification number as national identifier..."
                                }
                                required={dontOwnThis[i] ? null : "Please enter"}
                                maxLength={35}
                                onChange={(v) => {
                                    this.props.onNationalIdentifierChange(config.IdentifierType, v);
                                }}
                                disabled={dontOwnThis[i] || disabled}
                                regex={{
                                    errorMessage: data.Country == CountryCode.GBR ? "Incorrect NINO format" : null,
                                    value: data.Country == CountryCode.GBR ? NationalInsuranceNumber : null,
                                }}
                            />
                            {!mandatory && data.Country != CountryCode.ESP && (
                                <CheckboxEditor
                                    label="I don't own this identifier"
                                    value={dontOwnThis[i]}
                                    cssWrapper="control-size-60"
                                    onChange={(v) => {
                                        const _dontOwnThis = JSON.parse(JSON.stringify(dontOwnThis));
                                        _dontOwnThis[i] = v;
                                        let _threshold = i;
                                        if (v) {
                                            _threshold++;
                                        } else {
                                            // clear data
                                            this.clearData(i);
                                        }
                                        this.setState({ dontOwnThis: _dontOwnThis, renderThreshold: _threshold });
                                        if (v) {
                                            this.props.onNationalIdentifierChange(config.IdentifierType, null);
                                        }
                                    }}
                                    disabled={disabled}
                                />
                            )}
                        </>
                    );
                }
            }
        });

        return <>{result}</>;
    }

    clientTaxIdentifier(
        data: CustomerTaxResidencyModel,
        mifirCountryIdentifier: MifirCountryIdentifier,
        mandatory: boolean
    ): string {
        switch (mifirCountryIdentifier.IdentifierType) {
            case MifirIdentifierType.PassportNumber:
                if (mandatory) {
                    data.PassportNumber = data.PassportNumber ? data.PassportNumber : null;
                }
                return data.PassportNumber;
            case MifirIdentifierType.NationalIdentifier:
                return data.NationalIdentifier;
            case MifirIdentifierType.TaxNumber:
                return data.TaxReference;
            default:
                return null;
        }
    }
}
